<app-banner [title]="'Assurances'" [links]="links"></app-banner>

<div class="container mx-auto px-4 py-6">
	<!-- Search and Actions -->
	<div class="flex items-center justify-between gap-x-7 mb-4">
		<div class="flex items-center gap-x-2 mb-4">
			<label for="search" class="text-[12px]">Rechercher : </label>
			<input type="text" id="search" [value]="searchTerm" (input)="onSearchChange($event)"
				class="placeholder:text-gray-300 text-[12px] rounded-lg border border-gray-300 w-[300px] p-2"
				placeholder="Rechercher par numéro, garant ou statut..." />
		</div>
		<button (click)="refreshData()" [disabled]="loading"
			class="bg-primary text-white px-3 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 !text-[12px]">
			<lucide-icon *ngIf="loading" [img]="LoaderIcon" class="animate-spin h-4 w-4"></lucide-icon>
			<lucide-icon *ngIf="!loading" [img]="RefreshCw" class="h-4 w-4"></lucide-icon>
			Actualiser
		</button>
	</div>

	<!-- Error Message -->
	<div *ngIf="error" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
		<div class="flex items-center">
			<lucide-icon [img]="AlertCircleIcon" class="h-5 w-5 text-red-400 mr-2"></lucide-icon>
			<span class="text-red-800">{{ error }}</span>
		</div>
	</div>

	<!-- Loading State -->
	<div *ngIf="loading" class="flex justify-center items-center py-12">
		<div class="text-center">
			<lucide-icon [img]="LoaderIcon" class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4"></lucide-icon>
			<p class="text-gray-600">Chargement des assurances...</p>
		</div>
	</div>

	<!-- Data Table -->
	<div *ngIf="!loading" class="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow mt-7">
		<table class="w-full">
			<!-- head -->
			<thead>
				<tr class="bg-gray-100 border-b border-gray-200">
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">N° Adhérent</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">Garant</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">Date de souscription</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">Statut</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">Fin de souscription</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900"></th>
				</tr>
			</thead>
			<tbody>
				<tr *ngFor="let insurance of paginatedInsurances"
					class="hover:bg-gray-50 border-b border-gray-100 cursor-pointer transition-colors"
					(click)="navigateToDetails(insurance)">
					<td class="py-3 px-4 text-sm text-gray-900 font-medium">
						{{ insurance.policyNumber }}
					</td>
					<td class="py-3 px-4 text-sm text-gray-900 font-medium">{{ insurance.name }}</td>
					<td class="py-3 px-4 text-sm text-gray-900">
						{{ insurance.startDate }}
					</td>
					<td class="py-3 px-4">
						<span [class]="getStatusBadgeClass(insurance.status)">
							{{ getStatusText(insurance.status) }}
						</span>
					</td>
					<td class="py-3 px-4 text-sm text-gray-900">
						{{ insurance.endDate }}
					</td>
					<td class="py-3 px-4">
						<lucide-icon [img]="ChevronRightIcon" class="h-4 w-4 text-gray-400"></lucide-icon>
					</td>
				</tr>
			</tbody>
		</table>
	</div>

	<!-- Pagination -->
	<div *ngIf="!loading && filteredInsurances.length > 0"
		class="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
		<div class="text-sm text-gray-600 dark:text-gray-400">
			Affichage de {{ (currentPage - 1) * itemsPerPage + 1 }} à {{ Math.min(currentPage * itemsPerPage,
			filteredInsurances.length) }}
			sur {{ filteredInsurances.length }} résultats
		</div>

		<div class="flex items-center space-x-1">
			<button
				class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
				[disabled]="currentPage === 1" (click)="changePage(currentPage - 1)">
				<lucide-icon [img]="ChevronLeftIcon" class="h-4 w-4"></lucide-icon>
			</button>

			<button *ngFor="let page of pages" class="px-3 py-2 text-sm font-medium border border-gray-300 hover:bg-gray-50"
				[class]="page === currentPage ? 'bg-primary text-white border-primbg-primary' : 'bg-white text-gray-700'"
				(click)="changePage(page)">
				{{ page }}
			</button>

			<button
				class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
				[disabled]="currentPage === totalPages" (click)="changePage(currentPage + 1)">
				<lucide-icon [img]="ChevronRightIcon" class="h-4 w-4"></lucide-icon>
			</button>
		</div>
	</div>

	<!-- Empty state -->
	<div *ngIf="!loading && filteredInsurances.length === 0" class="text-center py-12">
		<div class="max-w-md mx-auto">
			<lucide-icon class="mx-auto h-12 w-12 text-gray-400" [img]="FileText"></lucide-icon>
			<h3 class="mt-2 text-sm font-medium text-gray-900">
				{{ searchTerm ? 'Aucun résultat trouvé' : 'Aucune assurance' }}
			</h3>
			<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
				{{ searchTerm ? 'Aucune assurance ne correspond à votre recherche.' : 'Aucune assurance trouvée pour les
				critères sélectionnés.' }}
			</p>
			<button *ngIf="searchTerm" (click)="searchTerm = ''; currentPage = 1" class="mt-4 btn btn-primary">
				Effacer la recherche
			</button>
		</div>
	</div>
</div>